import { useMemo, useState } from 'react';
import DemoBadge from '@/components/feature/DemoBadge';
import RevenueDashboard from '@/pages/demo/revenue/components/RevenueDashboard';
import PipelineStages from '@/pages/demo/revenue/components/PipelineStages';
import CharityFundraising from './CharityFundraising';
import RegionCoverage from './RegionCoverage';
import {
  SPONSOR_PACKAGES,
  TOURNAMENT_PROMOTIONS,
  PIPELINE_FUNNEL,
  SPONSOR_ORDERS,
  TOURNAMENT_ORDERS,
  REGISTRATION_GROSS,
  REGISTRATION_CONCEPT,
  REGISTRATION_FEE_TOTAL,
  REGISTRATION_GROSS_TOTAL,
  PLATFORM_FEE_RATE,
  packageById,
  promotionById,
  REGION_BY_ID,
  formatUSD,
  ctr,
} from '@/pages/demo/revenue/components/revenueData';

type Tab = 'pipeline' | 'coverage' | 'packages' | 'promotions' | 'registration' | 'fundraising' | 'analytics' | 'renewals';

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: 'pipeline', label: 'Sponsorship pipeline', icon: 'ri-flow-chart' },
  { id: 'coverage', label: 'Region coverage', icon: 'ri-map-pin-2-line' },
  { id: 'packages', label: 'Advertising packages', icon: 'ri-award-line' },
  { id: 'promotions', label: 'Tournament promotions', icon: 'ri-trophy-line' },
  { id: 'registration', label: 'Registration revenue', icon: 'ri-user-received-line' },
  { id: 'fundraising', label: 'Charity fundraising', icon: 'ri-hand-heart-line' },
  { id: 'analytics', label: 'Campaign analytics', icon: 'ri-bar-chart-2-line' },
  { id: 'renewals', label: 'Renewals', icon: 'ri-refresh-line' },
];

export default function RevenueOperations() {
  const [tab, setTab] = useState<Tab>('pipeline');

  const renewals = useMemo(() => SPONSOR_ORDERS.filter((o) => o.renewsInDays > 0 && o.renewsInDays <= 30).sort((a, b) => a.renewsInDays - b.renewsInDays), []);
  const topPerformers = useMemo(() => [...SPONSOR_ORDERS].filter((o) => o.impressions > 0).sort((a, b) => b.leads - a.leads).slice(0, 5), []);
  const maxLeads = Math.max(...topPerformers.map((o) => o.leads), 1);
  const maxFunnel = Math.max(...PIPELINE_FUNNEL.map((f) => f.count), 1);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-heading font-extrabold uppercase text-xl md:text-2xl text-foreground-950">Sponsorships &amp; revenue</h2>
        <DemoBadge label="Sample data — not live" />
      </div>

      <div className="flex items-start gap-3 bg-accent-50 border border-accent-300 border-l-4 border-l-accent-500 rounded-md px-4 py-3">
        <span className="w-5 h-5 flex items-center justify-center text-accent-700 shrink-0 mt-0.5"><i className="ri-alert-line"></i></span>
        <p className="text-sm text-foreground-800">
          <strong className="font-semibold">Conceptual Revenue Demo — No Real Payments, Registrations, or Automated Campaigns.</strong> All figures are illustrative and reconcile against one sample ledger.
        </p>
      </div>

      <div className="flex flex-wrap gap-1 bg-background-100 border border-background-200 rounded-full p-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full font-label font-bold uppercase tracking-wider text-[11px] whitespace-nowrap cursor-pointer transition-colors ${tab === t.id ? 'bg-foreground-950 text-background-50' : 'text-foreground-600 hover:text-foreground-950'}`}
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className={t.icon}></i></span>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'pipeline' && (
        <div className="space-y-5">
          <PipelineStages />
          <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <p className="font-heading font-bold text-lg text-foreground-950">Pipeline flow — this month</p>
              <span className="font-label uppercase tracking-widest text-[10px] text-foreground-500">Sample counts</span>
            </div>
            <ul className="space-y-3">
              {PIPELINE_FUNNEL.map((f) => (
                <li key={f.stage}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-foreground-800">{f.stage}</span>
                    <span className="font-label font-semibold text-foreground-600">{f.count}</span>
                  </div>
                  <div className="mt-1.5 h-2.5 rounded-full bg-background-200 overflow-hidden">
                    <div className="h-full bg-primary-500 rounded-full" style={{ width: `${(f.count / maxFunnel) * 100}%` }}></div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === 'coverage' && <RegionCoverage />}

      {tab === 'packages' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {SPONSOR_PACKAGES.map((p) => (
            <div key={p.id} className={`rounded-lg border p-5 ${p.featured ? 'border-primary-300 bg-background-50' : 'border-background-200 bg-background-50'}`}>
              <div className="flex items-center justify-between">
                <span className="w-10 h-10 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center text-xl"><i className="ri-award-line"></i></span>
                {p.featured && <span className="font-label uppercase tracking-widest text-[9px] font-bold bg-accent-100 text-accent-900 border border-accent-300 px-2 py-0.5 rounded-sm">Popular</span>}
              </div>
              <h4 className="mt-4 font-heading font-bold text-lg text-foreground-950 leading-tight">{p.name}</h4>
              <p className="mt-1 font-label uppercase tracking-widest text-[10px] text-foreground-500">{p.tagline}</p>
              <p className="mt-3 font-heading font-extrabold text-2xl text-foreground-950">{p.price === null ? 'Custom' : formatUSD(p.price)}<span className="text-xs font-body font-normal text-foreground-500"> {p.cadence}</span></p>
              <ul className="mt-4 space-y-2">
                {p.perks.map((perk) => (
                  <li key={perk} className="flex items-start gap-2 text-sm text-foreground-600">
                    <span className="w-4 h-4 flex items-center justify-center text-primary-500 shrink-0 mt-0.5"><i className="ri-check-line"></i></span>{perk}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <p className="sm:col-span-2 xl:col-span-4 text-xs text-foreground-500">Illustrative pricing — no subscription is created and no card is stored.</p>
        </div>
      )}

      {tab === 'promotions' && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
            {TOURNAMENT_PROMOTIONS.map((p) => (
              <div key={p.id} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="w-10 h-10 rounded-md bg-accent-100 text-accent-800 flex items-center justify-center text-xl"><i className="ri-trophy-line"></i></span>
                <h4 className="mt-4 font-heading font-bold text-base text-foreground-950 leading-tight">{p.name}</h4>
                <p className="mt-1 font-label uppercase tracking-widest text-[10px] text-foreground-500">{p.tagline}</p>
                <p className="mt-3 font-heading font-extrabold text-2xl text-foreground-950">{p.price === null ? '3%' : formatUSD(p.price)}<span className="text-xs font-body font-normal text-foreground-500"> {p.duration}</span></p>
              </div>
            ))}
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
            <div className="px-5 py-3.5 border-b border-background-200"><p className="font-heading font-bold text-lg text-foreground-950">Promotion orders</p></div>
            <ul className="divide-y divide-background-200">
              {TOURNAMENT_ORDERS.map((o) => (
                <li key={o.id} className="px-5 py-3 flex items-center gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground-950 truncate">{o.tournament}</p>
                    <p className="text-xs text-foreground-500 truncate">{promotionById(o.promotionId)?.name} · {REGION_BY_ID[o.regionId].city}</p>
                  </div>
                  <span className="font-heading font-extrabold text-lg text-foreground-950">{formatUSD(o.amount)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === 'registration' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-5 space-y-5">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <p className="font-heading font-bold text-lg text-foreground-950">Registration revenue</p>
              <p className="mt-2 text-xs text-foreground-500">With the managed service, the platform keeps an illustrative {Math.round(PLATFORM_FEE_RATE * 100)}% fee on registrations it processes.</p>
              <div className="mt-4 grid grid-cols-3 gap-3">
                <div className="rounded-md bg-background-100 border border-background-200 px-3 py-3">
                  <p className="font-label uppercase tracking-widest text-[9px] text-foreground-500">Gross</p>
                  <p className="mt-1 font-heading font-extrabold text-lg text-foreground-950">{formatUSD(REGISTRATION_GROSS_TOTAL)}</p>
                </div>
                <div className="rounded-md bg-background-100 border border-background-200 px-3 py-3">
                  <p className="font-label uppercase tracking-widest text-[9px] text-foreground-500">Fees</p>
                  <p className="mt-1 font-heading font-extrabold text-lg text-accent-700">{formatUSD(REGISTRATION_FEE_TOTAL)}</p>
                </div>
                <div className="rounded-md bg-background-100 border border-background-200 px-3 py-3">
                  <p className="font-label uppercase tracking-widest text-[9px] text-foreground-500">Events</p>
                  <p className="mt-1 font-heading font-extrabold text-lg text-foreground-950">{REGISTRATION_GROSS.length}</p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-2 text-xs text-foreground-700 bg-secondary-50 border border-secondary-200 rounded-md px-3 py-2">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-line"></i></span>{REGISTRATION_CONCEPT.individual.label}: {formatUSD(REGISTRATION_CONCEPT.individual.example)}
                </span>
                <span className="inline-flex items-center gap-2 text-xs text-foreground-700 bg-secondary-50 border border-secondary-200 rounded-md px-3 py-2">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-group-line"></i></span>{REGISTRATION_CONCEPT.foursome.label}: {formatUSD(REGISTRATION_CONCEPT.foursome.example)}
                </span>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 rounded-lg border border-background-200 bg-background-50 overflow-hidden">
            <div className="px-5 py-3.5 border-b border-background-200"><p className="font-heading font-bold text-lg text-foreground-950">Gross handled by region</p></div>
            <ul className="divide-y divide-background-200">
              {REGISTRATION_GROSS.map((r) => (
                <li key={r.regionId} className="px-5 py-3 flex items-center gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground-950 truncate">{REGION_BY_ID[r.regionId].city}</p>
                    <p className="text-xs text-foreground-500">Illustrative platform fee {formatUSD(Math.round(r.gross * PLATFORM_FEE_RATE))}</p>
                  </div>
                  <span className="font-heading font-extrabold text-lg text-foreground-950">{formatUSD(r.gross)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === 'fundraising' && <CharityFundraising />}

      {tab === 'analytics' && <RevenueDashboard />}

      {tab === 'renewals' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <p className="font-heading font-bold text-lg text-foreground-950">Upcoming renewals</p>
            <p className="mt-1 text-xs text-foreground-500">Campaigns renewing within 30 days.</p>
            <ul className="mt-4 space-y-3">
              {renewals.map((o) => (
                <li key={o.id} className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-md bg-accent-100 text-accent-800 flex items-center justify-center font-heading font-extrabold text-sm shrink-0">{o.renewsInDays}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground-950 truncate">{o.sponsor}</p>
                    <p className="text-xs text-foreground-500 truncate">{packageById(o.packageId)?.name} · {REGION_BY_ID[o.regionId].city}</p>
                  </div>
                  <span className="font-label uppercase tracking-widest text-[10px] text-foreground-500 whitespace-nowrap">renews in {o.renewsInDays}d</span>
                </li>
              ))}
              {renewals.length === 0 && <li className="text-sm text-foreground-500">No renewals due in the next 30 days.</li>}
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <p className="font-heading font-bold text-lg text-foreground-950">Sponsor performance</p>
            <p className="mt-1 text-xs text-foreground-500">Top campaigns by leads generated (sample).</p>
            <ul className="mt-4 space-y-4">
              {topPerformers.map((o) => (
                <li key={o.id}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-foreground-800 truncate">{o.sponsor}</span>
                    <span className="font-label font-semibold text-foreground-600 whitespace-nowrap">{o.leads} leads · {ctr(o.impressions, o.clicks)} CTR</span>
                  </div>
                  <div className="mt-1.5 h-2 rounded-full bg-background-200 overflow-hidden">
                    <div className="h-full bg-primary-500 rounded-full" style={{ width: `${(o.leads / maxLeads) * 100}%` }}></div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}